import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface BusinessErrorBoundaryProps {
  children: ReactNode;
}

interface BusinessErrorBoundaryState {
  hasError: boolean;
}

export class BusinessErrorBoundary extends Component<
  BusinessErrorBoundaryProps,
  BusinessErrorBoundaryState
> {
  state: BusinessErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): BusinessErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[BusinessErrorBoundary]", error, info.componentStack);
    fetch("/bff/error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        message: error.message,
        stack: error.stack?.slice(0, 1000) ?? null,
        url: window.location.pathname,
      }),
    }).catch(() => {});
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Stack
        component="main"
        spacing={2}
        sx={{
          minHeight: "100dvh",
          px: 3,
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography component="h1" variant="h4">
          Something went wrong
        </Typography>
        <Typography color="text.secondary">
          An unexpected error occurred. Reload the page to try again.
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload page
        </Button>
      </Stack>
    );
  }
}
